import { useMediaQuery, Theme, Box } from "@mui/material";
import { addYears, startOfDay } from "date-fns";
import { useEffect, useMemo } from "react";
import { keepPreviousData } from "@tanstack/react-query";
import { useListAndSearchAllInvestments } from "@rentablo/api/apiv1";
import { usePortfolioStatisticsByAssetClass } from "@api/v1/hooks/performance/usePortfolioStatisticsByAssetClass";
import { AssetTypeCard } from "./AssetTypeCard/AssetTypeCard";
import {
  AssetClassPills,
  assetClassesIn,
  useAssetClassFilter,
} from "./assetClassFilter";
import { useAssetTypeData } from "./useAssetTypeData";

export interface Props {
  accountIds: number[];
  currency?: string;
}

// The asset class cards; each one toggles the page's asset class filter.
export const AssetTypeBreakdown = ({ accountIds, currency = "EUR" }: Props) => {
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const startDate = startOfDay(addYears(new Date(), -1));
  const { activeClasses, toggle, setAvailableClasses } = useAssetClassFilter();

  const {
    allHoldings,
    funds,
    etfs,
    derivative,
    bonds,
    crypto,
    currencies,
    preciousMetals,
    isFetching,
    isPending,
  } = usePortfolioStatisticsByAssetClass({
    accountIds,
    startDate,
    displayCurrency: currency,
  });
  // A card shows only if the portfolio currently holds that asset class. The
  // same list query as the investments tab, so no extra request.
  const {
    data: investmentsResult,
    isPending: isInvestmentsPending,
    isFetching: isInvestmentsFetching,
  } = useListAndSearchAllInvestments(
    { accountId: accountIds, includeHistoric: false },
    { placeholderData: keepPreviousData }
  );
  const heldClasses = useMemo(
    () => assetClassesIn(investmentsResult?.investments ?? []),
    [investmentsResult]
  );
  // The previous depot's investments stay while the next ones load; their
  // classes must not reach the filter.
  const isLoading =
    isFetching || isPending || isInvestmentsPending || isInvestmentsFetching;

  const assetTypeData = useAssetTypeData({
    allHoldings,
    funds,
    etfs,
    derivative,
    bonds,
    crypto,
    currencies,
    preciousMetals,
    currency,
  });

  const filteredData = useMemo(() => {
    return isLoading
      ? assetTypeData
      : assetTypeData.filter(
          (data) =>
            heldClasses.includes(data.key) &&
            data.hasData &&
            data.amount != null &&
            data.percent != null &&
            data.increase != null
        );
  }, [assetTypeData, heldClasses, isLoading]);

  // Another depot may not have every class that is active.
  useEffect(() => {
    if (!isLoading) {
      setAvailableClasses(filteredData.map((data) => data.key));
    }
  }, [isLoading, filteredData, setAvailableClasses]);

  if (!filteredData.length) return null;

  if (isSm) {
    return (
      <AssetClassPills
        items={filteredData}
        activeClasses={activeClasses}
        onToggle={toggle}
        currency={currency}
        isLoading={isLoading}
      />
    );
  }

  return (
    <Box display="flex" flexDirection="row" flexWrap="wrap" gap={2.5} mb={7}>
      {filteredData.map((data) => (
        <AssetTypeCard
          key={data.key}
          isFetching={isLoading}
          title={data.title}
          amount={data.amount}
          increase={data.increase}
          percent={data.percent}
          changeAbs={data.changeAbs}
          color={data.color}
          currency={currency}
          active={activeClasses.includes(data.key)}
          onClick={() => toggle(data.key)}
        />
      ))}
    </Box>
  );
};
